import React, { useCallback, useEffect, useState } from 'react';
import ZoomControls from './ZoomControls';
import useAppStore from '../store/appStore';

const SunIcon = ({ size = 16 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
);

const MoonIcon = ({ size = 16 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
);

/** Wie Mandanten-App: html.dark ↔ pe-theme; Surfaces folgen pe-* Tokens. */
const TeamPortalChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const zoomLevel = useAppStore((s) => s.zoomLevel);
    const [isDarkTheme, setIsDarkTheme] = useState(() =>
        typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
    );

    useEffect(() => {
        const sync = () => setIsDarkTheme(document.documentElement.classList.contains('dark'));
        sync();
        const obs = new MutationObserver(sync);
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);

    const toggleTheme = useCallback(() => {
        const root = document.documentElement;
        const nextIsDark = !root.classList.contains('dark');
        if (nextIsDark) {
            root.classList.add('dark');
            localStorage.setItem('pe-theme', 'dark');
        } else {
            root.classList.remove('dark');
            localStorage.setItem('pe-theme', 'light');
        }
        setIsDarkTheme(nextIsDark);
    }, []);

    return (
        <>
            <div className="fixed right-0 top-24 md:top-28 z-[60] flex flex-col items-end gap-2 pointer-events-none print:hidden">
                <button
                    type="button"
                    onClick={toggleTheme}
                    title={isDarkTheme ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
                    aria-label={isDarkTheme ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln'}
                    className="pointer-events-auto hidden md:flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-l-2xl border-2 border-r-0 border-pe-border-strong bg-pe-card dark:bg-gradient-to-l dark:from-[#1a1d24] dark:to-[#0f1015] text-pe-muted dark:text-slate-300 shadow-lg hover:text-pe-accent-text dark:hover:text-[#E6C299] hover:border-[#8D6435]/50 dark:hover:border-[#E6C299]/50 hover:scale-[1.03] transition-all duration-200"
                >
                    {isDarkTheme ? <SunIcon size={16} /> : <MoonIcon size={16} />}
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] [writing-mode:vertical-rl] rotate-180">
                        {isDarkTheme ? 'Light' : 'Dark'}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={toggleTheme}
                    title={isDarkTheme ? 'Light Mode' : 'Dark Mode'}
                    aria-label={isDarkTheme ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln'}
                    className="pointer-events-auto md:hidden fixed left-3 bottom-[4.25rem] z-[10000] flex items-center gap-1.5 px-3 py-2 rounded-full border border-pe-border-strong bg-pe-card text-pe-muted text-[10px] font-black uppercase tracking-widest shadow-lg"
                >
                    {isDarkTheme ? <SunIcon size={12} /> : <MoonIcon size={12} />}
                    {isDarkTheme ? 'Light' : 'Dark'}
                </button>
            </div>

            <div
                className="min-h-screen bg-pe-bg text-pe-text"
                style={{
                    colorScheme: isDarkTheme ? 'dark' : 'light',
                    // iOS Safari: CSS zoom macht die Seite oft leer. Nur Desktop zoomen.
                    ...(/iPhone|iPad|iPod/i.test(navigator.userAgent || '')
                        ? { minHeight: '100dvh' }
                        : {
                              zoom: `${zoomLevel}%`,
                              minHeight:
                                  zoomLevel > 0 && zoomLevel !== 100
                                      ? `${(100 / zoomLevel) * 100}vh`
                                      : '100vh',
                          }),
                }}
            >
                {children}
            </div>

            <ZoomControls />
        </>
    );
};

export default TeamPortalChrome;
